<template>
  <v-card tile class="fill-height">
    <v-card tile flat class="pl-2">
      <!-- Top Toolbar -->
      <v-card-title v-if="!story" class="px-2 py-4">
        <!-- GROUPING (now fully functional) -->
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mr-2" small color="primary" outlined v-bind="attrs" v-on="on">
              Grouping: {{ group.text }}
              <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="option in visibleGroupOptions" :key="option.index">
              <v-btn text block small class="justify-start" @click="loadData(option.index)">
                {{ option.text }}
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- ANALYSIS -->
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mr-2" small color="primary" outlined v-bind="attrs" v-on="on">
              Analysis <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-if="isPublisherOrSupplier">
              <v-btn small block text class="justify-start mr-2" @click="showHideClusters()">
                {{ hideClusters ? 'Show Cluster Mapping' : 'Hide Cluster Mapping' }}
              </v-btn>
            </v-list-item>
            <v-list-item>
              <v-btn small block text class="justify-start" @click="showHideAnalysis()">
                {{ showAnalysis ? 'Hide Rolling 12M Analysis' : 'Show Rolling 12M Analysis' }}
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- SELECTION -->
        <v-menu offset-y class="ml-2 pl-2">
          <template v-slot:activator="{ on, attrs }">
            <v-btn :disabled="selected.length <= 0" small color="primary" outlined v-bind="attrs" v-on="on">
              Selection <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item>
              <v-btn small block text class="justify-start" :disabled="selected.length <= 0"
                @click="showSelected = true">
                View Selected
              </v-btn>
            </v-list-item>
            <v-list-item>
              <v-btn small block text class="justify-start" :disabled="selected.length <= 0"
                @click="exclude(group.value, selected)">
                Exclude from Search
              </v-btn>
            </v-list-item>
            <v-list-item>
              <div class="justify-start">
                <cluster-select class="py-0" :large="true" @clearSelections="clearSelections()" :disabled="notSelected"
                  :entityType="group.value" :entityIDType="group.value + '_id'" :entities="selectedIDs" />
              </div>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- SPACER -->
        <v-spacer></v-spacer>

        <!-- ICON BUTTONS -->
        <span class="ml-2">
          <v-btn small tile icon
            href="https://docs.tendertrace.com/guide/user-documentation/analysing-search-results/leaderboard"
            target="_blank">
            <v-icon color="secondary">mdi-help-circle-outline</v-icon>
          </v-btn>
        </span>

        <span class="ml-2">
          <v-btn small icon tile color="secondary" @click="exportTable()">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </span>

        <span class="ml-2">
          <v-tooltip bottom dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn small icon tile v-on="on" color="secondary" @click="openWidget()">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <span>Open in separate window</span>
          </v-tooltip>
        </span>

        <span class="ml-2 mr-5">
          <v-tooltip bottom dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn small icon tile v-on="on" color="secondary"
                @click="set_chart_object(); updateBoards(); sharebox = !sharebox">
                <v-icon>mdi-playlist-plus</v-icon>
              </v-btn>
            </template>
            <span>Add leaderboard to a custom report</span>
          </v-tooltip>
        </span>

      </v-card-title>

    </v-card>
    <v-skeleton-loader v-if="loading" type="table-tbody"></v-skeleton-loader>
    <v-data-table id="top_entities" v-else v-model="selected"
      :show-select="!story && (group.value == 'publisher' || group.value == 'supplier')" :items="dataview.data"
      item-key="id" :items-per-page="10" :headers="dataview.headers" class="elevation-1 px-2" @item-selected="select"
      @toggle-select-all="select_all">
      <template #item.publisher="{ item }">
        <div class="py-2">
          <router-link target="_blank" class="font-weight-medium caption"
            :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
            {{ item[group.value].name }}
          </router-link>
          <div class="caption">
            <span v-for="clstr in item.clusters" :key="clstr.id">
              <router-link :to="{ name: 'cluster', params: { clusterId: clstr.id } }">
                <v-chip v-if="clstr" class="my-0 ml-0 mr-2" color="primary" label text-color="primary" x-small outlined>
                  <v-icon x-small left>
                    mdi-hexagon-multiple
                  </v-icon>
                  {{ clstr.name }}
                </v-chip>
              </router-link>
            </span>
          </div>
          <div class="caption grey--text text--darken-1">
            <span v-if="item.supplier_cluster_members > 0">{{ item.supplier_cluster_members }} suppliers in my
              clusters</span>
          </div>
        </div>
      </template>
      <template #item.category="{ item }">
        <router-link target="_blank" class="font-weight-medium caption"
          :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
          {{ item[group.value].name }}
        </router-link>
      </template>
      <template #item.supplier="{ item }">
        <div class="py-2">
          <router-link target="_blank" class="font-weight-medium caption"
            :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
            {{ item[group.value].name }}
          </router-link>
          <div class="caption">
            <span v-for="clstr in item.clusters" :key="clstr.id">
              <router-link target="_blank" :to="{ name: 'cluster', params: { clusterId: clstr.id } }">
                <v-chip v-if="clstr" class="my-0 ml-0 mr-2" color="primary" label text-color="primary" x-small outlined>
                  <v-icon x-small left>
                    mdi-hexagon-multiple
                  </v-icon>
                  {{ clstr.name }}
                </v-chip>
              </router-link>
            </span>
          </div>
          <div class="caption grey--text text--darken-1">
            <span v-if="item.common_entities > 0">{{ item.common_entities }} common accounts | </span>
            <span v-if="item.prospect_list_accounts > 0">{{ item.prospect_list_accounts }} in my prospect list accounts
              | </span>
            <span v-if="item.cluster_buyers > 0">{{ item.cluster_buyers }} accounts in my buyer clusters</span>
          </div>
        </div>
      </template>
      <template #item.keyword="{ item }">
        <span class="font-weight-medium caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.government="{ item }">
        <span class="font-weight-medium caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.son_ref="{ item }">
      <router-link target="_blank" class="font-weight-medium caption" :to="{ name: 'panel', query: { 'son_id': item.son_ref, 'government': item.metadata.government } }">
        <span v-if="item.metadata.panel_title">{{ item.metadata.panel_title  }}</span>
        <span v-else>{{ item[group.value]  }}</span>
        </router-link>
      </template>
      <template #item.supplier_cluster="{ item }">
        <span class="font-weight-medium caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.buyer_cluster="{ item }">
        <span class="font-weight-medium caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.doc_count="{ item }">
        <router-link target="_blank" class="font-weight-normal caption" v-if="group.value == 'keyword'"
          :to="{ name: 'search', query: { ...$route.query, 'tender': item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link target="_blank" class="font-weight-normal caption" v-else-if="group.value == 'supplier_cluster'"
          :to="{ name: 'search', query: { ...$route.query, 'supplier_cluster_name': item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link target="_blank" class="font-weight-normal caption" v-else-if="group.value == 'buyer_cluster'"
          :to="{ name: 'search', query: { ...$route.query, 'buyer_cluster_name': item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link target="_blank" class="font-weight-normal caption" v-else-if="group.value == 'government'"
          :to="{ name: 'search', query: { ...$route.query, [group.value]: item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link target="_blank" class="font-weight-normal caption" v-else-if="group.value == 'son_ref'"
          :to="{ name: 'search', query: { ...$route.query, [group.value]: item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link target="_blank" class="font-weight-normal caption" v-else
          :to="{ name: 'search', query: { ...$route.query, [group.value + '_id']: item[group.value].id, viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
      </template>
      <template #item.value="{ item }">
        <span class="font-weight-normal caption">${{ item.value }}M</span>
      </template>

      <template #item.avg_contract="{ item }">
        <span class="font-weight-normal caption">${{ item.avg_contract }}M</span>
      </template>
      <template #item.per_awards="{ item }">
        <v-progress-linear :value="item.per_awards" color="primary" height="20">
          <template v-slot:default="{ value }">
            <strong class="font-weight-normal caption">{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
      </template>
      <template #item.per_award_value="{ item }">
        <v-progress-linear :value="item.per_award_value" color="rgb(255, 231, 10)" height="20">
          <template v-slot:default="{ value }">
            <strong class="font-weight-normal caption">{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
      </template>

      <template #item.current_year_total="{ item }">
        <span class="font-weight-normal caption"><span v-if="item.current_year_total > 0">${{ item.current_year_total
        }}M</span></span>
      </template>
      <template #item.spend_change_percent="{ item }">
        <div v-if="item.spend_change_percent" class="font-weight-normal caption" style="color:#12253a;">
          <v-icon :color="growth_format(item.spend_change_percent).color">
            {{ growth_format(item.spend_change_percent).icontype }}
          </v-icon>
          {{ growth_format(item.spend_change_percent).change }}%
        </div>
      </template>

      <template #item.curr_yr_avg_contract="{ item }">
        <span class="font-weight-normal caption" style="color:#12253a;"><span v-if="item.curr_yr_avg_contract > 0">${{
          item.curr_yr_avg_contract }}M</span></span>
      </template>
      <template #item.avg_change_percent="{ item }">
        <div v-if="item.avg_change_percent" class="font-weight-normal caption" style="color:#12253a;">
          <v-icon :color="growth_format(item.avg_change_percent).color">
            {{ growth_format(item.avg_change_percent).icontype }}
          </v-icon>
          {{ growth_format(item.avg_change_percent).change }}%
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="sharebox" hide-overlay max-width="1200" absolute style="overflow-x:'hidden' !important;">
      <v-card tile>
        <v-card-title>Add leaderboard to a custom report
          <div class="flex-grow-1"></div> <v-btn icon @click="sharebox = !sharebox"><v-icon
              medium>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card tile flat class="mx-auto pa-5" max-width="1150px">
          <v-row justify="center">
            <v-col cols="6" class="pb-0 d-flex child-flex">
              <v-combobox v-model="selected_board" :items="storyboards" :loading="isLoading" item-value="id"
                :search-input.sync="search" item-text="plainTextTitle" label="Select a report" outlined dense
                class="pb-0" return-object>
              </v-combobox>
            </v-col>
            <v-col cols="6" class="pb-0">

              <v-btn color="primary" :disabled="!saveButtonActive"
                @click="saveChart(), updateBoards(), sharebox = !sharebox">Save leaderboard to report</v-btn>
            </v-col>

            <v-col cols="12" class="pt-0 d-flex child-flex">
              <story :changed="embed_obj_changed" :key="embed_obj_changed" :data="embed_object" />
            </v-col>
          </v-row>
        </v-card>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSelected" width="50%">
      <v-card>
        <v-list flat>
          <span><v-subheader>Currently selected entities</v-subheader></span><span>
            <v-btn class="caption ml-2" x-small text color="primary" :disabled="selected.length <= 0"
              @click="(selectAll = false), user_clicked_select_all(), showSelected = false">
              Clear All
            </v-btn></span>
          <v-btn absolute top right icon @click="showSelected = false" medium>
            <v-icon medium>
              mdi-close
            </v-icon>
          </v-btn>

          <v-card flat class="px-2 py-2 mx-2 my-2" min-height="300" max-height="420"
            style="flex-grow: 1 !important; overflow:auto !important;">

            <v-row align="left" justify="start">
              <v-col v-for="entity in selectedEntities" :key="entity.id" class="py-1">
                <v-chip color="primary" small label clearable dense close dark @click:close="deselectEntity(entity.id)">
                  {{ entity[group.value].name }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card>

        </v-list>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import { bus } from "../main.js";
import moment from "moment";
//import tableToJSON from "tabletojson";
import { growth_format, exportCSVFile, getCurrencySymbol } from "./Utils.js";
import { entitySearch } from "./APIManager.js";
import { fetchStoryboards, addStory, addStoryboard } from "../components/APIManager"
import Story from "../components/Story.vue";
import ClusterSelect from "../components/ClusterSelect.vue";

export default {
  props: {
    story: {
      type: Boolean,
      default: false
    },
    chart_options: Object,
    changed: Number
  },
  components: {
    Story,
    ClusterSelect
  },

  data() {
    return {
      selectAll: false,
      selectedEntities: [],
      selected: [],
      selectedCount: 0,
      showSelected: false,
      isLoading: false,
      sharebox: false,
      widget_name: "leaderboard",
      entity_aggs: [],
      defaultButton: 0,
      currentIndex: 0,
      publisher_aggs: [],
      keyword_aggs: [],
      supplier_aggs: [],
      category_aggs: [],
      supplier_cluster_aggs: [],
      buyer_cluster_aggs: [],
      govt_aggs: [],
      panel_aggs: [],
      avg_contract_value: Number,
      total_amount: Number,
      updated: 0,
      loading: Boolean,
      //row_number: [50],
      num_entities: process.env.VUE_APP_LEADERBOARD_SIZE_LIMIT,
      total_records: Number,
      num_publishers: Number,
      num_suppliers: Number,
      num_categories: Number,
      num_keywords: Number,
      num_supplier_clusters: Number,
      num_buyer_clusters: Number,
      num_govts: Number,
      other_categories: Number,
      other_suppliers: Number,
      other_publishers: Number,
      group_options: [
        { text: "Buyer", value: "publisher", alt_value: "entity", visible: true },
        { text: "Category", value: "category", alt_value: "category", visible: true },
        { text: "Supplier", value: "supplier", alt_value: "supplier", visible: true },
        { text: "Keyword", value: "keyword", alt_value: "keyword", visible: true },
        { text: "Government", value: "government", alt_value: "government", visible: true },
        { text: "Supplier Cluster", value: "supplier_cluster", alt_value: "supplier_cluster", visible: true },
        { text: "Buyer Cluster", value: "buyer_cluster", alt_value: "buyer_cluster", visible: true },
        { text: "Panels", value: "son_ref", alt_value: "son_ref", visible: true }
      ],
      group: { text: "Buyer", value: "publisher", alt_value: "entity" },
      dataview: { headers: [], data: [] },
      storydata: {},
      embed_object: {},
      storyboards: [],
      selected_board: null,
      resetChart: Boolean,
      new_board: null,
      search: null,
      saveButtonActive: false,
      embed_obj_changed: Number,
      hideClusters: true,
      showAnalysis: false

    };
  },

  watch: {
    $route(to, from) {
      this.refresh();
    },
    search(val, prev) {
      if (this.selected_board || this.search != null) {
        this.saveButtonActive = true;
      }
      else {
        this.saveButtonActive = false;
      }
    }
  },

  created() {
    ////////////////////console.log("SEARCH TERMS: ", JSON.stringify(this.search_terms));
    //this.updated = (Math.random() + 10) * Math.random();
  },

  async mounted() {
    this.loading = true;
    if (this.story) {
      this.loadStory();
    } else {
      const idx = this.$route.query.leaderboardIndex;
      if (idx) {
        this.defaultButton = idx;
        this.loadData(idx);
      }
      else {
        this.loadData(this.currentIndex);
      }

    }

  },

  methods: {

    growth_format,

    extractNames(data) {
      let names = [];

      // Helper function to process a single object
      function processObject(obj) {
        // Check for "publisher" or "supplier" key and extract its name
        if (obj.publisher && obj.publisher.name) {
          names.push(obj.publisher.name);
        }
        if (obj.supplier && obj.supplier.name) {
          names.push(obj.supplier.name);
        }
      }

      // Handle arrays or single objects
      if (Array.isArray(data)) {
        data.forEach(item => processObject(item));
      } else {
        processObject(data);
      }

      return names;
    },

    showHideClusters() {
      const qry = { ...this.$route.query };
      this.hideClusters = !this.hideClusters;

      //////console.log(this.hideClusters);
      qry.hide_clusters = this.hideClusters;
      if (JSON.stringify(qry) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ path: "/search", query: qry });
      }
    },

    showHideAnalysis() {
      const qry = { ...this.$route.query };
      this.showAnalysis = !this.showAnalysis;

      //////console.log(this.hideClusters);
      qry.yoy_analysis = this.showAnalysis;
      if (JSON.stringify(qry) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ path: "/search", query: qry });
      }
    },

    exclude(entityType, selectedEntities) {
      // Extract names to exclude
      const excludeList = this.extractNames(selectedEntities);

      // Copy the current query parameters
      const qry = { ...this.$route.query };

      // Format the entityType key for the query
      entityType = `exclude_${entityType}`;

      // Append or initialize the exclusion list in the query
      if (qry[entityType]) {
        qry[entityType] = [...new Set([...qry[entityType], ...excludeList])];
      } else {
        qry[entityType] = excludeList;
      }

      // Check if the new query is the same as the current query to prevent NavigationDuplicated error
      if (JSON.stringify(qry) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ path: "/search", query: qry });
      }
    },

    silentURLUpdate() {

      //quietly update the URL without triggering a route changed event
      const url = new URL(window.location);

      url.searchParams.set("leaderboardIndex", this.currentIndex);
      //url.searchParams.set("hide_clusters", this.hideClusters); 

      window.history.pushState({}, '', url);
    },

    clearSelections() {
      this.selected = [];
      this.selectAll = false;
      this.selectedEntities = [];

    },

    async updateBoards() {
      this.isLoading = true;
      this.search = null;
      this.saveButtonActive = false;

      var data = await fetchStoryboards();
      this.storyboards = data.storyboards;

      for (var i in this.storyboards) {
        this.storyboards[i]["plainTextTitle"] = this.stripHTML(this.storyboards[i]["title"]);
      }

      this.isLoading = false;

    },

    select(val) {
      //////console.log(val);
      if (val.value == true) {
        this.selected.push(val.item);
        this.updateSelectedEntities();
      }
      else {
        this.deselectEntity(val.item.id)
      }
    },

    /* select(val) {
      if (val.value === true) {
          // Check if the item is already in the selected array
          const isAlreadySelected = this.selected.find(item => item.id === val.id);
          
          // If the item is not already selected, add it to the selected array
          if (!isAlreadySelected) {
              this.selected.push(val.item);
              this.updateSelectedEntities();
          }
          
      }
      else {
          this.deselectEntity(val.item.id);
      }
    }, */

    select_all(lst) {
      if (lst.value == true) {
        this.selected = lst.items;
        this.updateSelectedEntities();
      }
      else {
        this.selected = [];
        this.selectedEntities = [];
      }
    },

    user_clicked_select_all() {
      this.userClickedSelectAll = this.selectAll;
      if (!this.selectAll) { //if the user has physically unclicked selectall
        this.selected = [];
        this.selectedEntities = [];
      }
    },

    updateSelectedEntities(value) {
      ////////console.log(value)
      for (let s in this.selected) {

        const isAlreadySelected = this.selectedEntities.find(item => item.id === this.selected[s].id);

        // If the item is not already selected, add it to the selected array
        if (!isAlreadySelected) {
          this.selectedEntities.push(this.selected[s]);
        }
        //check the entity id is not already in the selected entities

      }
    },

    deselectEntity(id) {
      let index = this.selected.findIndex(x => x.id === id);
      //////////////////console.log (index,  this.entities[entity].id);
      if (index >= 0) { // only splice array when item is found
        this.selected.splice(index, 1); // 2nd parameter means remove one item only
      }

      let entityIndex = this.selectedEntities.findIndex(x => x.id === id);

      if (entityIndex >= 0) { // only splice array when item is found
        this.selectedEntities.splice(entityIndex, 1); // 2nd parameter means remove one item only
      }

      this.selectAll = false;

    },

    stripHTML(text) {
      let strippedText = text.replace(/(<([^>]+)>)/gi, "");
      return strippedText;
    },

    set_chart_object() {
      this.storydata["defaultButton"] = this.currentIndex;
      this.embed_object["title"] = this.title;
      this.embed_object["description"] = this.description;
      this.embed_object["chart_type"] = "leaderboard";
      this.embed_object["payload"] = { imgData: {}, options: this.storydata }
      this.embed_object["id"] = (Math.random() + Date.now()) * Math.random();
      this.embed_obj_changed = (Math.random() + Date.now()) * Math.random();
    },

    async saveChart() {

      let data = {};
      data["title"] = this.embed_object.title;
      data["description"] = this.embed_object.description;
      data["chart_type"] = this.embed_object.chart_type;
      data["payload"] = this.embed_object.payload;

      if (!this.selected_board || (this.search != this.selected_board["plainTextTitle"])) {
        //this.selected_board = this.addItem(this.search);
        await addStoryboard({ title: this.search, description: 'This report needs an overview', story: data })
      }
      else {
        await addStory(this.selected_board.id, data)
        ////////////////////console.log("EXISTING REPORT: ", this.selected_board);
      }

      ////////////////////console.log("LEADER BOARD PAYLOAD: ", data);

    },

    getPercent(value, total) {
      return (value / total * 100).toFixed(1)
    },

    buildTable() {
      // Base headers always present
      this.dataview.headers = [
        { text: this.group.text, value: this.group.value, sortable: false, width: '20%' },
        { text: "# Notices", value: 'doc_count', align: 'center' },
        { text: "Value", value: 'value', align: 'center' },
        { text: "Avg", value: 'avg_contract', align: 'center' },
        { text: "% Notices", value: 'per_awards', align: 'center' },
        { text: "% Value", value: 'per_award_value', align: 'center' }
      ];

      var growthExists = false;

      // First check if any growth exists
      for (var item in this.entity_aggs) {
        var grp_value = this.group.value;

        if (grp_value === "keyword" || grp_value === "government" || grp_value === "son_ref" || grp_value === "supplier_cluster" || grp_value === "buyer_cluster") {
          if (this.entity_aggs[item] && this.entity_aggs[item].growth) {
            growthExists = true;
            break;
          }
        } else {
          if (this.entity_aggs[item] &&
            this.entity_aggs[item][grp_value] &&
            this.entity_aggs[item][grp_value].buckets &&
            this.entity_aggs[item][grp_value].buckets[0] &&
            this.entity_aggs[item][grp_value].buckets[0].growth) {
            growthExists = true;
            break;
          }
        }
      }

      if (growthExists) {
        this.dataview.headers.push(
          { text: "12M Val", value: 'current_year_total', align: 'center' },
          { text: "12M Chg", value: 'spend_change_percent', align: 'center' },
          { text: "12M Avg", value: 'curr_yr_avg_contract', align: 'center' },
          { text: "12M Avg Chg", value: 'avg_change_percent', align: 'center' }
        );
      }

      for (var item in this.entity_aggs) {
        var grp_value = this.group.value;
        var row = {};

        var metadata = null;
        if (this.entity_aggs[item].top_info)
          metadata = this.getSourceFromTopInfo(this.entity_aggs[item].top_info);

        if (grp_value === "keyword" || grp_value === "government" || grp_value === "son_ref" || grp_value === "supplier_cluster" || grp_value === "buyer_cluster") {
          row.metadata = metadata;
          row[grp_value] = this.entity_aggs[item].key;
          row.doc_count = this.entity_aggs[item].doc_count;
          row.value = (this.entity_aggs[item].total_amount.value / 1000000).toFixed(2);
          row.avg_contract = (this.entity_aggs[item].total_amount.value / this.entity_aggs[item].doc_count / 1000000).toFixed(2);
          row.per_awards = this.getPercent(this.entity_aggs[item].doc_count, this.total_records);
          row.per_award_value = (this.total_amount < 1) ? 'N/A' : this.getPercent(this.entity_aggs[item].total_amount.value, this.total_amount);

          var growth = {};
          if (this.entity_aggs[item] && this.entity_aggs[item].growth) {
            growth = this.entity_aggs[item].growth;
            row.current_year_total = ((growth.current_year_total || 0) / 1000000).toFixed(2);
            row.spend_change_percent = (growth.spend_change_percent != null) ? growth.spend_change_percent : null;
            row.curr_yr_avg_contract = ((growth.curr_yr_avg_contract || 0) / 1000000).toFixed(2);
            row.avg_change_percent = (growth.avg_change_percent != null) ? growth.avg_change_percent : null;
          }
        } else {
          var grp_id = this.entity_aggs[item].key;
          row.id = grp_id;

          if (!this.story && grp_value === "supplier") {
            row.common_entities = this.entity_aggs[item].common_entities;
            row.prospect_list_accounts = this.entity_aggs[item].prospect_list_accounts;
            row.cluster_buyers = this.entity_aggs[item].cluster_buyers;
            row.clusters = this.entity_aggs[item].clusters;
          }

          if (!this.story && grp_value === "publisher") {
            row.supplier_cluster_members = this.entity_aggs[item].supplier_cluster_members;
            row.clusters = this.entity_aggs[item].clusters;
          }

          row[grp_value] = { id: grp_id, name: this.entity_aggs[item][grp_value].buckets[0].key };
          row.doc_count = this.entity_aggs[item][grp_value].buckets[0].doc_count;
          row.value = (this.entity_aggs[item][grp_value].buckets[0].total_amount.value / 1000000).toFixed(2);
          row.avg_contract = (this.entity_aggs[item][grp_value].buckets[0].total_amount.value / this.entity_aggs[item][grp_value].buckets[0].doc_count / 1000000).toFixed(2);
          row.per_awards = this.getPercent(this.entity_aggs[item][grp_value].buckets[0].doc_count, this.total_records);
          row.per_award_value = (this.total_amount < 1) ? 'N/A' : this.getPercent(this.entity_aggs[item][grp_value].buckets[0].total_amount.value, this.total_amount);

          var growth = {};
          if (
            this.entity_aggs[item] &&
            this.entity_aggs[item][grp_value] &&
            this.entity_aggs[item][grp_value].buckets &&
            this.entity_aggs[item][grp_value].buckets[0] &&
            this.entity_aggs[item][grp_value].buckets[0].growth
          ) {
            growth = this.entity_aggs[item][grp_value].buckets[0].growth;
            row.current_year_total = ((growth.current_year_total || 0) / 1000000).toFixed(2);
            row.spend_change_percent = (growth.spend_change_percent != null) ? growth.spend_change_percent : null;
            row.curr_yr_avg_contract = ((growth.curr_yr_avg_contract || 0) / 1000000).toFixed(2);
            row.avg_change_percent = (growth.avg_change_percent != null) ? growth.avg_change_percent : null;
          }
        }

        this.dataview.data[item] = row;
      }
    },

    openWidget() {
      let routeData = this.$router.resolve({ name: this.widget_name, query: this.$route.query });
      window.open(routeData.href, '_blank');
    },

    sort(col) {
      var table, rows, switching, i, x, y, shouldSwitch;
      table = document.getElementById("top_entities").getElementsByTagName("TABLE")[0];
      ////////////////////console.log(table);
      switching = true;
      /*Make a loop that will continue until
      no switching has been done:*/
      while (switching) {
        //start by saying: no switching is done:
        switching = false;
        rows = table.rows;
        /*Loop through all table rows (except the
        first, which contains table headers):*/
        for (i = 1; i < (rows.length - 1); i++) {

          //start by saying there should be no switching:
          shouldSwitch = false;
          /*Get the two elements you want to compare,
          one from current row and one from the next:*/
          x = rows[i].getElementsByTagName("TD")[col];
          y = rows[i + 1].getElementsByTagName("TD")[col];
          //check if the two rows should switch place:
          if (x.innerHTML.toLowerCase() > y.innerHTML.toLowerCase()) {
            //if so, mark as a switch and break the loop:
            shouldSwitch = true;
            break;
          }
        }
        if (shouldSwitch) {
          /*If a switch has been marked, make the switch
          and mark that a switch has been done:*/
          rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
          switching = true;
        }
      }

    },

    exportTable() {
      const tmp = document.createElement("div");
      const table = document.querySelector('#top_entities').cloneNode(true);
      tmp.appendChild(table);

      const HtmlTableToJson = require('html-table-to-json');
      const jsonTable = HtmlTableToJson.parse(tmp.innerHTML);

      // Dynamically generate headers from the actual displayed table headers
      const headers = {};

      for (const col of this.dataview.headers) {
        switch (col.value) {
          case 'doc_count':
            headers['doc_count'] = '# Notices';
            break;
          case 'value':
            headers['value'] = '$ Value';
            break;
          case 'avg_contract':
            headers['avg_contract'] = 'Avg Contract';
            break;
          case 'per_awards':
            headers['per_awards'] = '% Awards';
            break;
          case 'per_award_value':
            headers['per_award_value'] = '% Award Value';
            break;
          case 'current_year_total':
            headers['current_year_total'] = `${getCurrencySymbol()} YoY Total`;
            break;
          case 'spend_change_percent':
            headers['spend_change_percent'] = `${getCurrencySymbol()} YoY Change`;
            break;
          case 'curr_yr_avg_contract':
            headers['curr_yr_avg_contract'] = `${getCurrencySymbol()} YoY Avg`;
            break;
          case 'avg_change_percent':
            headers['avg_change_percent'] = `${getCurrencySymbol()} YoY Avg Change`;
            break;
          default:
            headers[col.value] = this.group.text;  // fallback for group column
        }
      }

      try {
        const dte = new Date();
        const fileTitle = `tendertrace_leaderboard_${dte.toISOString()}`;
        exportCSVFile(headers, jsonTable.results[0], fileTitle);
      } catch (err) {
        //console.error("Export failed: ", err);
      }
    },

    getSourceFromTopInfo(topInfo) {
      if (
        topInfo &&
        topInfo.hits &&
        topInfo.hits.hits &&
        topInfo.hits.hits.length > 0
      ) {
        return topInfo.hits.hits[0]._source || null;
      }
      return null;
    },

    loadGroup(index) {
      ////////////////console.log("loading group: ", index)
      ////////////////console.log("this.group_options: ", this.group_options)
      this.loading = true;
      this.entity_aggs = [];
      this.dataview.headers = [];
      this.dataview.data = [];

      if (this.group_options[index].value == "category") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.category_aggs) {
          if (this.category_aggs[i].category.buckets.length == 0) {
            ////////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.category_aggs[i];
            new_index++;
          }
        }

      } else if (this.group_options[index].value == "publisher") {
        this.group = new Object;
        this.group = this.group_options[index];
        this.entity_aggs = this.publisher_aggs;
      }
      else if (this.group_options[index].value == "government") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.govt_aggs) {
          this.entity_aggs[new_index] = this.govt_aggs[i];
          new_index++;
        }
      }
      else if (this.group_options[index].value == "son_ref") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.panel_aggs) {
          this.entity_aggs[new_index] = this.panel_aggs[i];
          new_index++;
        }
      }
      else if (this.group_options[index].value == "keyword") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.keyword_aggs) {
          if (this.keyword_aggs.length == 0) {
            ////////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.keyword_aggs[i];
            new_index++;
          }
        }
      }
      else if (this.group_options[index].value == "buyer_cluster") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.buyer_cluster_aggs) {
          if (this.buyer_cluster_aggs.length == 0) {
            ////////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.buyer_cluster_aggs[i];
            new_index++;
          }
        }
      }
      else if (this.group_options[index].value == "supplier_cluster") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.supplier_cluster_aggs) {
          if (this.supplier_cluster_aggs.length == 0) {
            ////////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.supplier_cluster_aggs[i];
            new_index++;
          }
        }
      }
      else {
        this.group = new Object;
        this.group = this.group_options[index];
        this.entity_aggs = this.supplier_aggs;
      }

      ////////////////console.log("finished loading group...")

      this.buildTable();
      ////////////////console.log("finished building table...")
      this.loading = false;
    },

    entity_selected(entity_id) {
      //var ent = JSON.stringify(entity);
      this.$router.push({ name: this.group.value, params: { id: entity_id } });
      //bus.$emit("entity-selected", entity);
    },

    async refresh() {
      this.loading = true;
      if (this.story) {
        this.loadStory();
      } else {
        await this.loadData(this.currentIndex);
      }
    },

    async loadData(index) {
      this.clearSelections();
      this.currentIndex = index;
      this.defaultButton = index;
      this.silentURLUpdate();
      var query = JSON.parse(JSON.stringify(this.$route.query));

      this.loading = true;

      query["agg_size"] = this.num_entities;
      query["target_aggs"] = this.group_options[index].value;
      query["hide_clusters"] = this.hideClusters;
      query["yoy_analysis"] = this.showAnalysis;

      entitySearch(query, 1)
        .then(data => {
          //////////////////console.log("LEADERBOARD DATA: ", data);
          this.storydata = data;
          this.total_records = data.hits.value;
          this.num_publishers =
            data.aggregations.publisher_id.buckets.length +
            data.aggregations.publisher_id.sum_other_doc_count;
          this.num_govts = data.aggregations.government.buckets.length + data.aggregations.government.sum_other_doc_count;
          this.govt_aggs = data.aggregations.government.buckets;
          if (data.aggregations.son_ref) {
            this.panel_aggs = data.aggregations.son_ref.buckets;
          }
          this.publisher_aggs = data.aggregations.publisher_id.buckets;
          this.other_publishers = data.aggregations.publisher_id.sum_other_doc_count;

          if (data.aggregations.supplier_id) {
            this.supplier_aggs = data.aggregations.supplier_id.buckets;
          }

          if (data.aggregations.search_terms) {
            this.keyword_aggs = data.aggregations.search_terms.buckets;
            this.num_keywords = this.keyword_aggs.length;
          }
          else {
            this.keyword_aggs = [];
            this.num_keywords = Number;
          }

          if (data.aggregations.supplier_clusters) {
            this.supplier_cluster_aggs = data.aggregations.supplier_clusters.buckets;
            this.num_supplier_clusters = this.supplier_cluster_aggs.length;
          }
          else {
            this.supplier_cluster_aggs = [];
            this.num_supplier_clusters = Number;
          }

          if (data.aggregations.buyer_clusters) {
            this.buyer_cluster_aggs = data.aggregations.buyer_clusters.buckets;
            this.num_buyer_clusters = this.buyer_cluster_aggs.length;
          }
          else {
            this.buyer_cluster_aggs = [];
            this.num_buyer_clusters = Number;
          }

          this.other_suppliers = data.aggregations.supplier_id.sum_other_doc_count;
          this.num_suppliers =
            data.aggregations.supplier_id.buckets.length +
            data.aggregations.supplier_id.sum_other_doc_count;
          this.category_aggs = data.aggregations.category_id.buckets;
          this.other_categories = data.aggregations.category_id.sum_other_doc_count;
          this.num_categories =
            data.aggregations.category_id.buckets.length +
            data.aggregations.category_id.sum_other_doc_count;
          this.total_amount = data.aggregations.total_amount.value;
          //this.plain_text_query = data.plain_text_query;
          ////////////////////console.log(JSON.stringify(this.gov_aggs));
          this.loadGroup(index);
          this.loading = false;
        })
        .catch(error => {
          ////////////////////console.log(error);
          //this.errored = true;
          //this.overlay = false;
        });
    },

    loadStory() {

      let data = this.chart_options;

      this.total_records = data.hits.value;

      this.num_publishers =
        data.aggregations.publisher_id.buckets.length +
        data.aggregations.publisher_id.sum_other_doc_count;
      this.publisher_aggs = data.aggregations.publisher_id.buckets;
      this.other_publishers = data.aggregations.publisher_id.sum_other_doc_count;
      this.num_govts = data.aggregations.government.buckets.length + data.aggregations.government.sum_other_doc_count;
      this.govt_aggs = data.aggregations.government.buckets;
      if (data.aggregations.son_ref) {
        this.panel_aggs = data.aggregations.son_ref.buckets;
      }
      if (data.aggregations.supplier_id) {
        this.supplier_aggs = data.aggregations.supplier_id.buckets;
      }

      if (data.aggregations.search_terms) {
        this.keyword_aggs = data.aggregations.search_terms.buckets;
        this.num_keywords = this.keyword_aggs.length;
      }
      else {
        this.keyword_aggs = [];
        this.num_keywords = Number;
      }

      if (data.aggregations.supplier_clusters) {
        this.supplier_cluster_aggs = data.aggregations.supplier_clusters.buckets;
        this.num_supplier_clusters = this.supplier_cluster_aggs.length;
      }
      else {
        this.supplier_cluster_aggs = [];
        this.num_supplier_clusters = Number;
      }

      if (data.aggregations.buyer_clusters) {
        this.buyer_cluster_aggs = data.aggregations.buyer_clusters.buckets;
        this.num_buyer_clusters = this.buyer_cluster_aggs.length;

      }
      else {
        this.buyer_cluster_aggs = [];
        this.num_buyer_clusters = Number;
      }

      this.other_suppliers = data.aggregations.supplier_id.sum_other_doc_count;
      this.num_suppliers =
        data.aggregations.supplier_id.buckets.length +
        data.aggregations.supplier_id.sum_other_doc_count;
      this.category_aggs = data.aggregations.category_id.buckets;
      this.other_categories = data.aggregations.category_id.sum_other_doc_count;
      this.num_categories =
        data.aggregations.category_id.buckets.length +
        data.aggregations.category_id.sum_other_doc_count;
      this.total_amount = data.aggregations.total_amount.value;

      //this.defaultButton = 0;
      this.loadGroup(data.defaultButton);
      this.loading = false;

    },

    check_gov_name(gov) {
      if (gov === "AUS") {
        return "AUS";
      }
      else if (gov === "FED") {
        return "FED";
      }
      else if (gov === "DEF") {
        return "DEF";
      } else {
        return gov;
      }
    },

    numFormatter(num) {
      if (num > 999 && num < 1000000) {
        return (num / 1000).toFixed(1) + "K"; // convert to K for number from > 1000 < 1 million
      } else if (num > 1000000 && num < 1000000000) {
        return (num / 1000000).toFixed(1) + "M"; // convert to M for number from > 1 million
      } else if (num > 1000000000) {
        return (num / 1000000000).toFixed(1) + "B"; // convert to M for number from > 1 million
      } else if (num < 1000) {
        return num; // if value < 1000, nothing to do
      }
    },

    formattedCurrency: function (amount) {
      ////////////////////console.log("AMOUNT: " + amount);
      if (!isNaN(amount)) {
        return getCurrencySymbol() + this.numFormatter(amount);
        //return getCurrencySymbol() + Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } //else {
      //return null;
      //}
    },

    btnFormat(index) {
      if (index == this.currentIndex) {
        return "text-capitalize font-weight-black body-2 mt-0 mb-1"
      }
      else {
        return "text-capitalize body-2 mt-0 mb-1"
      }

    },

    formattedCurrencyFull: function (amount) {
      ////////////////////console.log("AMOUNT: " + amount);
      if (!isNaN(amount)) {
        return (
          getCurrencySymbol() +
          Math.round(amount)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        );
      } //else {
      //return null;
      //}
    },
    showHideDetails(item) {
      //this.item_details = !this.item_details;
      this.item_details.show = true;
      this.item_details.record = item;
      bus.$emit("show-hide-details", this.item_details);
    },
    formatDate(date_value) {
      return moment(date_value).format("MMMM YYYY");
    }
  },
  computed: {
    notSelected() {
      if (this.selected.length > 0) return false;
      else return true;
    },
    selectedIDs() {
      return this.selected.map(item => item.id);
    },
    isPublisherOrSupplier() {
      return (this.group.value === "publisher" || this.group.value === "supplier");
    },

    visibleGroupOptions() {
      return this.group_options
        .map((option, idx) => ({ ...option, index: idx })) // preserve original index
        .filter(option => option.visible);
    }
  }
};
</script>

<style>
.v-data-table-header th {
  white-space: nowrap;
}
</style>
